import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import qrcode from "qrcode-generator";
import { apiFetchJson } from "../api";
import { t, tf, locale } from "../i18n";
import { useModalPresence, overlayMotionProps, dialogMotionProps } from "../utils/modalPresence";
import { useEscapeLayer } from "../utils/escapeLayers";
import { CheckIcon, XIcon, Spinner, ExternalLinkIcon, RefreshIcon } from "./Icons";

// Оплата подписки прямо в панели. Тариф → промокод → чем платить → счёт в
// CryptoBot или xRocket. Счёт открывается в новой вкладке (обычно это
// Telegram), а окно ждёт оплату: раз в несколько секунд спрашивает статус, и
// как только деньги пришли, подписка уже включена — сервер делает это сам, по
// вебхуку или своей проверкой. Окну остаётся сказать об этом.
//
// Вид — классы pm-* в index.css, цвета из переменных темы.

const POLL_INTERVAL_MS = 5000;

const DAY_FORMS_RU = ["день", "дня", "дней"];
const daysLabel = (days) => {
  const value = Math.max(0, Math.round(Number(days) || 0));
  if (String(locale()).startsWith("en")) return `${value} ${value === 1 ? "day" : "days"}`;
  const mod10 = value % 10;
  const mod100 = value % 100;
  const form = mod10 === 1 && mod100 !== 11
    ? DAY_FORMS_RU[0]
    : (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? DAY_FORMS_RU[1] : DAY_FORMS_RU[2]);
  return `${value} ${form}`;
};

const formatMoney = (value) => {
  const number = Number(value) || 0;
  return number.toLocaleString(locale(), { minimumFractionDigits: Number.isInteger(number) ? 0 : 2, maximumFractionDigits: 2 });
};

const formatCountdown = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

// QR со ссылкой на счёт — оплатить с телефона, когда панель открыта на ПК.
const QrCode = ({ value, size = 168 }) => {
  const path = useMemo(() => {
    if (!value) return { d: "", count: 0 };
    const qr = qrcode(0, "M");
    qr.addData(value);
    qr.make();
    const count = qr.getModuleCount();
    let d = "";
    for (let row = 0; row < count; row += 1) {
      for (let col = 0; col < count; col += 1) {
        if (qr.isDark(row, col)) d += `M${col} ${row}h1v1h-1z`;
      }
    }
    return { d, count };
  }, [value]);
  if (!path.count) return null;
  const quiet = 2;
  const box = path.count + quiet * 2;
  return (
    <svg className="pm-qr" width={size} height={size} viewBox={`${-quiet} ${-quiet} ${box} ${box}`} shapeRendering="crispEdges" role="img" aria-label={t("QR-код для оплаты")}>
      <rect x={-quiet} y={-quiet} width={box} height={box} fill="#ffffff" rx="1.5" />
      <path d={path.d} fill="#0e1015" />
    </svg>
  );
};

export default function PaymentModal({ open, onClose, onPaid, initialPlanId = "", isPhoneView = false }) {
  const presence = useModalPresence(open);
  const [plans, setPlans] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [plansError, setPlansError] = useState("");
  const [planId, setPlanId] = useState("");
  const [providerId, setProviderId] = useState("");
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null); // { code, discountPercent, finalPriceUsd }
  const [promoState, setPromoState] = useState({ status: "idle", error: "" });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [invoice, setInvoice] = useState(null);
  const [payment, setPayment] = useState(null); // ответ /api/billing/payment/:id
  const [checking, setChecking] = useState(false);
  const [now, setNow] = useState(Date.now());
  const pollRef = useRef(null);

  // Каждое открытие — с начала: тарифы свежие, прошлый счёт забыт.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setInvoice(null);
    setPayment(null);
    setCreateError("");
    setPromoInput("");
    setPromo(null);
    setPromoState({ status: "idle", error: "" });
    setLoadingPlans(true);
    setPlansError("");
    apiFetchJson("/api/plans", { timeoutMs: 15000, retry: false })
      .then((data) => {
        if (!alive) return;
        const list = (Array.isArray(data?.items) ? data.items : [])
          .filter((plan) => Number(plan?.days) > 0 && Number(plan?.priceUsd) > 0)
          .sort((a, b) => Number(a.days) - Number(b.days));
        const enabled = Array.isArray(data?.paymentProviders) ? data.paymentProviders : [];
        setPlans(list);
        setProviders(enabled);
        const preferred = list.find((plan) => plan.id === initialPlanId);
        const best = list.length
          ? list.reduce((acc, plan) => (Number(plan.priceUsd) / Number(plan.days) < Number(acc.priceUsd) / Number(acc.days) ? plan : acc), list[0])
          : null;
        setPlanId((preferred || best || list[0] || {}).id || "");
        setProviderId((enabled[0] || {}).id || "");
        if (!list.length) setPlansError(t("Тарифы сейчас недоступны. Попробуйте позже."));
      })
      .catch((error) => { if (alive) setPlansError(error?.message || t("Не удалось загрузить тарифы")); })
      .finally(() => { if (alive) setLoadingPlans(false); });
    return () => { alive = false; };
  }, [open, initialPlanId]);

  const plan = plans.find((item) => item.id === planId) || null;
  const bestValueId = useMemo(() => (plans.length > 1
    ? plans.reduce((acc, item) => (Number(item.priceUsd) / Number(item.days) < Number(acc.priceUsd) / Number(acc.days) ? item : acc), plans[0]).id
    : ""), [plans]);

  // Промокод проверяется под выбранный тариф: другой тариф — проверить заново.
  const applyPromo = useCallback(async (code, targetPlanId) => {
    const normalized = String(code || "").trim();
    if (!normalized || !targetPlanId) return;
    setPromoState({ status: "loading", error: "" });
    try {
      const data = await apiFetchJson("/api/promo/validate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: normalized, planId: targetPlanId }),
        timeoutMs: 15000,
        retry: false
      });
      setPromo({ code: data.code || normalized, discountPercent: Number(data.discountPercent) || 0, finalPriceUsd: Number(data.finalPriceUsd) || 0, planId: targetPlanId });
      setPromoState({ status: "ok", error: "" });
    } catch (error) {
      setPromo(null);
      setPromoState({ status: "error", error: t(String(error?.message || "Промокод недействителен")) });
    }
  }, []);

  useEffect(() => {
    if (promo && planId && promo.planId !== planId) applyPromo(promo.code, planId);
  }, [planId, promo, applyPromo]);

  const finalPrice = promo && promo.planId === planId ? promo.finalPriceUsd : Number(plan?.priceUsd || 0);

  const stopPolling = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const checkPayment = useCallback(async (paymentId, { manual = false } = {}) => {
    if (!paymentId) return;
    if (manual) setChecking(true);
    try {
      const data = await apiFetchJson(`/api/billing/payment/${encodeURIComponent(paymentId)}`, { timeoutMs: 20000, retry: false });
      setPayment(data);
      if (data?.status === "paid" || data?.status === "expired" || data?.status === "cancelled") stopPolling();
    } catch {
      // Сеть моргнула — следующий опрос спросит снова.
    } finally {
      if (manual) setChecking(false);
    }
  }, []);

  // Ждём оплату: опрос статуса и тикающий таймер счёта.
  useEffect(() => {
    if (!open || !invoice?.paymentId) return undefined;
    stopPolling();
    pollRef.current = window.setInterval(() => checkPayment(invoice.paymentId), POLL_INTERVAL_MS);
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => {
      stopPolling();
      window.clearInterval(tick);
    };
  }, [open, invoice?.paymentId, checkPayment]);

  // Вернулись на вкладку после оплаты в Telegram — проверить сразу.
  useEffect(() => {
    if (!open || !invoice?.paymentId) return undefined;
    const onVisible = () => { if (document.visibilityState === "visible") checkPayment(invoice.paymentId); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [open, invoice?.paymentId, checkPayment]);

  const status = payment?.status || (invoice ? "pending" : "");
  const paid = status === "paid";
  const expiresAtMs = Date.parse(String(payment?.expiresAt || invoice?.expiresAt || "")) || 0;
  const leftMs = expiresAtMs ? expiresAtMs - now : 0;
  const expired = status === "expired" || (invoice && !paid && expiresAtMs && leftMs <= 0);

  const openInvoice = (url) => {
    const target = String(url || invoice?.payUrl || "").trim();
    if (target) window.open(target, "_blank", "noopener,noreferrer");
  };

  const createInvoice = async () => {
    if (!plan || creating) return;
    setCreating(true);
    setCreateError("");
    // Вкладка открывается сразу по нажатию: открытая после ответа сервера,
    // она попала бы под блокировку всплывающих окон.
    const popup = window.open("", "_blank");
    try {
      const data = await apiFetchJson("/api/billing/create-invoice", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planId: plan.id, promoCode: promo && promo.planId === plan.id ? promo.code : "", provider: providerId }),
        timeoutMs: 30000,
        retry: false
      });
      setInvoice(data);
      setPayment(null);
      setNow(Date.now());
      if (popup && !popup.closed) {
        popup.opener = null;
        popup.location.href = data.payUrl;
      } else {
        openInvoice(data.payUrl);
      }
    } catch (error) {
      if (popup && !popup.closed) popup.close();
      setCreateError(t(String(error?.message || "Не удалось создать счёт")));
    } finally {
      setCreating(false);
    }
  };

  const close = () => {
    if (paid) onPaid?.(payment?.subscription || null);
    onClose?.();
  };

  useEscapeLayer(open, close);

  if (!presence.mounted || typeof document === "undefined") return null;

  const paidUntil = payment?.subscription?.expiresAt
    ? new Date(payment.subscription.expiresAt).toLocaleDateString(locale(), { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" })
    : "";

  return createPortal(
    <div className="modal-overlay pm-overlay" {...overlayMotionProps(presence.state)} onClick={(event) => { if (event.target === event.currentTarget) close(); }}>
      <div className="pm-card" role="dialog" aria-modal="true" aria-label={t("Оплата подписки")} {...dialogMotionProps(presence.state)}>
        <div className="pm-head">
          <div style={{ minWidth: 0 }}>
            <div className="pm-title">{paid ? t("Подписка активна") : invoice ? t("Ожидаем оплату") : t("Оплата подписки")}</div>
            <div className="pm-sub">
              {paid
                ? (paidUntil ? tf`Доступ открыт до ${paidUntil}` : t("Доступ открыт"))
                : invoice
                  ? tf`${invoice.providerTitle || ""} · счёт на ${formatMoney(invoice.amountUsd)} USDT`
                  : t("Оплата в USDT через CryptoBot или xRocket")}
            </div>
          </div>
          <button type="button" className="ui-icon-btn" onClick={close} aria-label={t("Закрыть")} title={t("Закрыть")}>
            <XIcon size={16} />
          </button>
        </div>

        {paid ? (
          <div className="pm-body pm-done">
            <span className="pm-done-icon"><CheckIcon size={26} /></span>
            <div className="pm-done-title">{t("Оплата получена")}</div>
            <div className="pm-done-text">
              {invoice?.plan?.title ? tf`Тариф ${invoice.plan.title}` : ""}
              {paidUntil ? ` · ${tf`до ${paidUntil}`}` : ""}
            </div>
            <button type="button" className="ui-btn-primary pm-wide" onClick={close}>{t("Перейти в панель")}</button>
          </div>
        ) : invoice ? (
          <div className="pm-body">
            <div className={`pm-wait${isPhoneView ? " is-phone" : ""}`}>
              {!isPhoneView && !expired && <QrCode value={invoice.payUrl} />}
              <div className="pm-wait-info">
                <div className="pm-amount">
                  {formatMoney(invoice.amountUsd)}
                  <small>USDT</small>
                </div>
                <div className="pm-wait-line">
                  {expired ? (
                    <span className="pm-badge is-bad">{t("Счёт истёк")}</span>
                  ) : (
                    <>
                      <Spinner size={13} />
                      <span>{t("Ждём оплату")}</span>
                      {expiresAtMs > 0 && <span className="pm-timer">{formatCountdown(leftMs)}</span>}
                    </>
                  )}
                </div>
                <p className="pm-hint">
                  {expired
                    ? t("Срок счёта вышел. Создайте новый — тариф и промокод сохранятся.")
                    : isPhoneView
                      ? t("Счёт открылся в Telegram. Оплатите его и вернитесь сюда — подписка включится сама.")
                      : t("Счёт открылся в новой вкладке. Можно оплатить и с телефона — отсканируйте QR. Подписка включится сама.")}
                </p>
              </div>
            </div>
            <div className="pm-actions">
              {expired ? (
                <button type="button" className="ui-btn-primary pm-wide" onClick={() => { setInvoice(null); setPayment(null); }}>
                  {t("Создать новый счёт")}
                </button>
              ) : (
                <>
                  <button type="button" className="ui-btn-primary" onClick={() => openInvoice()}>
                    <ExternalLinkIcon size={15} />
                    {tf`Открыть в ${invoice.providerTitle || "Telegram"}`}
                  </button>
                  <button type="button" className="ui-btn-secondary" onClick={() => checkPayment(invoice.paymentId, { manual: true })} disabled={checking}>
                    {checking ? <Spinner size={14} /> : <RefreshIcon size={15} />}
                    {t("Проверить оплату")}
                  </button>
                </>
              )}
              <button type="button" className="pm-link" onClick={() => { setInvoice(null); setPayment(null); }}>
                {t("Изменить тариф")}
              </button>
            </div>
          </div>
        ) : (
          <div className="pm-body">
            {loadingPlans ? (
              <div className="pm-loading"><Spinner size={18} /></div>
            ) : plansError ? (
              <div className="pm-error">{plansError}</div>
            ) : (
              <>
                <div className="pm-plans" role="radiogroup" aria-label={t("Тариф")}>
                  {plans.map((item) => {
                    const active = item.id === planId;
                    const perDay = Number(item.priceUsd) / Math.max(1, Number(item.days));
                    return (
                      <button
                        key={item.id}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className={`pm-plan${active ? " is-active" : ""}`}
                        onClick={() => setPlanId(item.id)}
                      >
                        {item.id === bestValueId && <span className="pm-plan-badge">{t("Выгоднее всего")}</span>}
                        <span className="pm-plan-days">{daysLabel(item.days)}</span>
                        <span className="pm-plan-price">{formatMoney(item.priceUsd)}<small>USDT</small></span>
                        <span className="pm-plan-per-day">{tf`≈ ${perDay.toLocaleString(locale(), { maximumFractionDigits: perDay < 10 ? 1 : 0 })} в день`}</span>
                        <span className="pm-plan-check" aria-hidden="true">{active ? <CheckIcon size={12} /> : null}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="pm-row">
                  <div className="pm-label">{t("Промокод")}</div>
                  <form
                    className="pm-promo"
                    onSubmit={(event) => { event.preventDefault(); applyPromo(promoInput, planId); }}
                  >
                    <input
                      value={promoInput}
                      onChange={(event) => {
                        setPromoInput(event.target.value);
                        if (promo) setPromo(null);
                        if (promoState.status !== "idle") setPromoState({ status: "idle", error: "" });
                      }}
                      placeholder={t("Если есть")}
                      spellCheck={false}
                      autoComplete="off"
                    />
                    <button type="submit" className="ui-btn-secondary" disabled={!promoInput.trim() || promoState.status === "loading"}>
                      {promoState.status === "loading" ? <Spinner size={14} /> : t("Применить")}
                    </button>
                  </form>
                  {promoState.status === "ok" && promo && (
                    <div className="pm-note is-ok">{tf`Скидка ${promo.discountPercent}% применена`}</div>
                  )}
                  {promoState.status === "error" && <div className="pm-note is-bad">{promoState.error}</div>}
                </div>

                {providers.length > 1 && (
                  <div className="pm-row">
                    <div className="pm-label">{t("Чем оплатить")}</div>
                    <div className="pm-providers" role="radiogroup">
                      {providers.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          role="radio"
                          aria-checked={providerId === item.id}
                          className={`pm-provider${providerId === item.id ? " is-active" : ""}`}
                          onClick={() => setProviderId(item.id)}
                        >
                          {item.title || item.id}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {providers.length === 0 && (
                  <div className="pm-error">{t("Оплата временно недоступна. Напишите в поддержку.")}</div>
                )}

                {createError && <div className="pm-error">{createError}</div>}

                <div className="pm-total">
                  <div>
                    <div className="pm-label">{t("К оплате")}</div>
                    <div className="pm-total-sum">
                      {promo && promo.planId === planId && Number(plan?.priceUsd) !== finalPrice && (
                        <s>{formatMoney(plan?.priceUsd)}</s>
                      )}
                      {formatMoney(finalPrice)}
                      <small>USDT</small>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="ui-btn-primary pm-pay"
                    onClick={createInvoice}
                    disabled={!plan || creating || providers.length === 0}
                  >
                    {creating ? <Spinner size={14} /> : null}
                    {creating ? t("Создаём счёт...") : t("Оплатить")}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
